import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    CalendarCheck, ChevronLeft, ChevronRight, Plus, X,
    Loader2, AlertCircle, Car, Clock, Trash2,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    fetchFleetCalendar,
    createFleetBooking,
    cancelFleetBooking,
    clearFleetBookingErrors,
    selectFleetMyBookings,
    selectFleetOccupiedSlots,
    selectFleetCalendarLoading,
    selectFleetCalendarError,
    selectFleetBookingCreating,
    selectFleetBookingCreateError,
    selectFleetBookingCancellingUuid,
    selectFleetBookingCancelError,
    fetchFleetServiceCategories,
    selectFleetServiceCategories,
    fetchFleetServiceProfiles,
    selectFleetServiceProfiles,
} from '../store/slices/fleetBookingSlice';
import {
    fetchFleetVehicles,
    selectFleetVehicles,
} from '../store/slices/fleetVehicleSlice';
import Layout from './Layout';

// ============================================
// Date helpers
// ============================================

const iso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Monday-first grid of the month, padded to whole weeks. */
const buildMonthGrid = (year, month) => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);

    // JS getDay(): 0=Sun … 6=Sat. Shift so Monday is 0.
    const leading = (first.getDay() + 6) % 7;

    const cells = [];
    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
};

const STATUS_STYLES = {
    SCHEDULED:        'bg-emerald-50 text-emerald-700 border-emerald-200',
    VEHICLE_RECEIVED: 'bg-blue-50 text-blue-700 border-blue-200',
    JOB_STARTED:      'bg-amber-50 text-amber-700 border-amber-200',
    COMPLETED:        'bg-gray-100 text-gray-600 border-gray-200',
    CANCELLED:        'bg-red-50 text-red-600 border-red-200',
    NO_SHOW:          'bg-red-50 text-red-600 border-red-200',
};

const statusLabel = (s) =>
    (s || '').split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

// ============================================
// New booking dialog
// ============================================

const NewBookingDialog = ({ open, date, vehicles, serviceCategories, serviceProfiles, onClose, onSubmit, submitting, error }) => {
    // mode: 'PACKAGE' → book a ServiceProfile; 'ADHOC' → pick individual services.
    // The backend treats these as mutually exclusive and rejects a request that
    // carries both, so the form only ever sends one.
    const [form, setForm] = useState({
        vehicleUuid: '',
        scheduledTime: '09:00',
        mode: 'PACKAGE',
        serviceProfileUuid: '',
        serviceType: '',
        description: '',
    });
    const [localError, setLocalError] = useState(null);

    useEffect(() => {
        if (open) {
            setForm({
                vehicleUuid: vehicles.length === 1 ? vehicles[0].uuid : '',
                scheduledTime: '09:00',
                // Default to packages when the workshop has any, otherwise ad-hoc.
                mode: serviceProfiles.length > 0 ? 'PACKAGE' : 'ADHOC',
                serviceProfileUuid: '',
                serviceType: '',
                description: '',
            });
            setLocalError(null);
        }
    }, [open, vehicles, serviceProfiles]);

    if (!open) return null;

    const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

    const selectedCategory = serviceCategories.find((sc) => sc.value === form.serviceType);
    const selectedProfile = serviceProfiles.find((sp) => sp.uuid === form.serviceProfileUuid);

    /** "Toyota Camry · 60,000 km" — the band a package applies to. */
    const profileBand = (p) => {
        const bits = [p.make, p.model, p.vehicleYear].filter(Boolean).join(' ');
        const km = p.mileageKm ? `${p.mileageKm.toLocaleString()} km` : null;
        return [bits, km].filter(Boolean).join(' · ');
    };

    const handleSubmit = () => {
        setLocalError(null);
        if (!form.vehicleUuid) return setLocalError('Choose which vehicle to book in.');

        const isPackage = form.mode === 'PACKAGE';

        if (isPackage && !form.serviceProfileUuid) {
            return setLocalError('Choose a service package.');
        }
        if (!isPackage && !form.serviceType) {
            return setLocalError('Choose what service you need.');
        }

        onSubmit({
            vehicleUuid: form.vehicleUuid,
            scheduledDate: iso(date),
            scheduledTime: form.scheduledTime || null,
            // Exactly one path — the backend rejects a request carrying both.
            ...(isPackage
                ? { serviceProfileUuid: form.serviceProfileUuid }
                // `type` must be the category enum name; the backend rejects anything
                // that isn't a service this workshop actually offers.
                : { serviceTypes: [{ type: form.serviceType }] }),
            description: form.description.trim() || null,
        });
    };

    const inputCls =
        'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={submitting ? undefined : onClose} />

            <div className="relative bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100">
                            <CalendarCheck size={18} className="text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-display font-semibold text-gray-900">Book a service</h2>
                            <p className="text-xs text-gray-500">
                                {date.toLocaleDateString('en-AU', {
                                    weekday: 'long', day: 'numeric', month: 'long',
                                })}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    {vehicles.length === 0 ? (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                            <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-amber-700">
                                Add a vehicle to your fleet first — you'll find it under My Vehicles.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Vehicle</label>
                                <select value={form.vehicleUuid} onChange={set('vehicleUuid')} className={inputCls}>
                                    <option value="">Choose a vehicle…</option>
                                    {vehicles.map((v) => (
                                        <option key={v.uuid} value={v.uuid}>
                                            {v.displayName || `${v.year} ${v.make} ${v.model} (${v.rego})`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Drop-off time
                                </label>
                                <input
                                    type="time"
                                    value={form.scheduledTime}
                                    onChange={set('scheduledTime')}
                                    className={inputCls}
                                />
                            </div>

                            {/*
                              Package vs individual services. The two are mutually
                              exclusive — the backend rejects a request carrying both —
                              so the toggle drives which one is sent.
                              The toggle is only shown when the workshop actually has
                              packages; otherwise ad-hoc is the only option.
                            */}
                            {serviceProfiles.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        What do you need?
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[
                                            { value: 'PACKAGE', label: 'Service package' },
                                            { value: 'ADHOC',   label: 'Individual service' },
                                        ].map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => setForm((p) => ({
                                                    ...p,
                                                    mode: opt.value,
                                                    // Clear the other path so we never send both.
                                                    serviceProfileUuid: '',
                                                    serviceType: '',
                                                }))}
                                                className={[
                                                    'px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors cursor-pointer',
                                                    form.mode === opt.value
                                                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
                                                ].join(' ')}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {form.mode === 'PACKAGE' && serviceProfiles.length > 0 ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Service package
                                    </label>
                                    <select
                                        value={form.serviceProfileUuid}
                                        onChange={set('serviceProfileUuid')}
                                        className={inputCls}
                                    >
                                        <option value="">Choose a package…</option>
                                        {serviceProfiles.map((sp) => (
                                            <option key={sp.uuid} value={sp.uuid}>
                                                {sp.name}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedProfile && profileBand(selectedProfile) && (
                                        <p className="text-xs text-gray-500 mt-1.5">
                                            {profileBand(selectedProfile)}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Service
                                    </label>
                                    <select
                                        value={form.serviceType}
                                        onChange={set('serviceType')}
                                        className={inputCls}
                                    >
                                        <option value="">Choose a service…</option>
                                        {serviceCategories.map((sc) => (
                                            <option key={sc.value} value={sc.value}>
                                                {sc.label}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedCategory?.description && (
                                        <p className="text-xs text-gray-500 mt-1.5">
                                            {selectedCategory.description}
                                        </p>
                                    )}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Notes <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <textarea
                                    value={form.description}
                                    onChange={set('description')}
                                    rows={3}
                                    placeholder="Anything the workshop should know"
                                    className={`${inputCls} resize-none`}
                                />
                            </div>
                        </>
                    )}

                    {(localError || error) && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                            <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-600">{localError || error}</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || vehicles.length === 0}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold cursor-pointer disabled:opacity-60 border-none"
                    >
                        {submitting && <Loader2 size={16} className="animate-spin" />}
                        {submitting ? 'Booking…' : 'Book service'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// Day detail panel
// ============================================

const DayPanel = ({ date, bookings, occupiedCount, onBook, onCancel, cancellingUuid, cancelError }) => {
    if (!date) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <p className="text-sm text-gray-500 text-center py-8">
                    Select a day to see your bookings.
                </p>
            </div>
        );
    }

    const isPast = iso(date) < iso(new Date());

    return (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
                <div>
                    <h3 className="font-display font-semibold text-gray-900">
                        {date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </h3>
                    {occupiedCount > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5">
                            {occupiedCount} other {occupiedCount === 1 ? 'booking' : 'bookings'} in the workshop
                        </p>
                    )}
                </div>
                {!isPast && (
                    <button
                        onClick={onBook}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold cursor-pointer border-none flex-shrink-0"
                    >
                        <Plus size={16} />
                        Book
                    </button>
                )}
            </div>

            <div className="p-5 space-y-3">
                {cancelError && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                        <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{cancelError}</p>
                    </div>
                )}

                {bookings.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">
                        You have no bookings on this day.
                    </p>
                ) : (
                    bookings.map((b) => (
                        <div key={b.uuid} className="border border-gray-200 rounded-xl p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium border ${STATUS_STYLES[b.status] || STATUS_STYLES.COMPLETED}`}>
                                            {statusLabel(b.status)}
                                        </span>
                                        <span className="text-xs text-gray-400 font-mono">{b.bookingCode}</span>
                                    </div>

                                    <p className="text-sm font-medium text-gray-900 mt-2 flex items-center gap-1.5">
                                        <Car size={14} className="text-gray-400 flex-shrink-0" />
                                        {b.vehicleDisplayName || b.vehicleRego}
                                    </p>

                                    {b.scheduledTime && (
                                        <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5">
                                            <Clock size={14} className="text-gray-400" />
                                            {b.scheduledTime.slice(0, 5)}
                                        </p>
                                    )}

                                    {b.serviceTypes?.length > 0 && (
                                        <p className="text-sm text-gray-600 mt-1.5">
                                            {b.serviceTypes.map((s) => s.type).join(', ')}
                                        </p>
                                    )}

                                    {b.description && (
                                        <p className="text-sm text-gray-500 mt-1">{b.description}</p>
                                    )}
                                </div>

                                {b.cancellable && (
                                    <button
                                        onClick={() => onCancel(b)}
                                        disabled={cancellingUuid === b.uuid}
                                        title="Cancel booking"
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50 flex-shrink-0"
                                    >
                                        {cancellingUuid === b.uuid
                                            ? <Loader2 size={16} className="animate-spin" />
                                            : <Trash2 size={16} />}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// ============================================
// Fleet bookings calendar page
// ============================================

const FleetBookingsPage = () => {
    const dispatch = useAppDispatch();

    const myBookings = useAppSelector(selectFleetMyBookings);
    const occupiedSlots = useAppSelector(selectFleetOccupiedSlots);
    const loading = useAppSelector(selectFleetCalendarLoading);
    const error = useAppSelector(selectFleetCalendarError);
    const creating = useAppSelector(selectFleetBookingCreating);
    const createError = useAppSelector(selectFleetBookingCreateError);
    const cancellingUuid = useAppSelector(selectFleetBookingCancellingUuid);
    const cancelError = useAppSelector(selectFleetBookingCancelError);
    const vehicles = useAppSelector(selectFleetVehicles);
    const serviceCategories = useAppSelector(selectFleetServiceCategories);
    const serviceProfiles = useAppSelector(selectFleetServiceProfiles);

    const today = new Date();
    const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
    const [selected, setSelected] = useState(today);
    const [dialogOpen, setDialogOpen] = useState(false);

    const loadMonth = useCallback((year, month) => {
        const start = new Date(year, month, 1);
        const end = new Date(year, month + 1, 0);
        dispatch(fetchFleetCalendar({ startDate: iso(start), endDate: iso(end) }));
    }, [dispatch]);

    useEffect(() => {
        loadMonth(cursor.year, cursor.month);
    }, [cursor, loadMonth]);

    // Vehicles and service categories both feed the booking dialog's dropdowns.
    useEffect(() => {
        dispatch(fetchFleetVehicles({ page: 0, size: 100, rego: '' }));
        dispatch(fetchFleetServiceCategories());
        dispatch(fetchFleetServiceProfiles());
    }, [dispatch]);

    // Index bookings + occupancy by ISO date for O(1) cell lookup.
    const byDate = useMemo(() => {
        const map = {};
        for (const b of myBookings) {
            (map[b.scheduledDate] ??= { mine: [], occupied: 0 }).mine.push(b);
        }
        for (const s of occupiedSlots) {
            (map[s.scheduledDate] ??= { mine: [], occupied: 0 }).occupied += 1;
        }
        return map;
    }, [myBookings, occupiedSlots]);

    const cells = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);

    const shiftMonth = (delta) => {
        const d = new Date(cursor.year, cursor.month + delta, 1);
        setCursor({ year: d.getFullYear(), month: d.getMonth() });
    };

    const handleCreate = async (payload) => {
        const result = await dispatch(createFleetBooking(payload));
        if (createFleetBooking.fulfilled.match(result)) {
            setDialogOpen(false);
            dispatch(clearFleetBookingErrors());
            loadMonth(cursor.year, cursor.month);
        }
    };

    const handleCancel = async (booking) => {
        const result = await dispatch(cancelFleetBooking(booking.uuid));
        if (cancelFleetBooking.fulfilled.match(result)) {
            loadMonth(cursor.year, cursor.month);
        }
    };

    const selectedKey = selected ? iso(selected) : null;
    const selectedDay = selectedKey ? byDate[selectedKey] : null;

    return (
        <Layout>
            <div className="px-6 py-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100">
                        <CalendarCheck size={22} className="text-emerald-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-display font-bold text-gray-900">My Bookings</h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Book your vehicles in and track upcoming services.
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                        <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Calendar */}
                    <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <h2 className="font-display font-semibold text-gray-900">
                                {MONTHS[cursor.month]} {cursor.year}
                            </h2>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => shiftMonth(-1)}
                                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <button
                                    onClick={() => {
                                        const n = new Date();
                                        setCursor({ year: n.getFullYear(), month: n.getMonth() });
                                        setSelected(n);
                                    }}
                                    className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer text-sm font-medium"
                                >
                                    Today
                                </button>
                                <button
                                    onClick={() => shiftMonth(1)}
                                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="py-24 flex flex-col items-center justify-center">
                                <Loader2 size={28} className="text-emerald-500 animate-spin" />
                                <p className="mt-3 text-sm text-gray-500">Loading your calendar…</p>
                            </div>
                        ) : (
                            <div className="p-4">
                                {/* Day labels */}
                                <div className="grid grid-cols-7 gap-1 mb-1">
                                    {DAY_LABELS.map((d) => (
                                        <div key={d} className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wider py-1">
                                            {d}
                                        </div>
                                    ))}
                                </div>

                                {/* Cells */}
                                <div className="grid grid-cols-7 gap-1">
                                    {cells.map((date, i) => {
                                        if (!date) return <div key={`pad-${i}`} />;

                                        const key = iso(date);
                                        const day = byDate[key];
                                        const mine = day?.mine ?? [];
                                        const occupied = day?.occupied ?? 0;

                                        const isToday = key === iso(today);
                                        const isSelected = key === selectedKey;

                                        return (
                                            <button
                                                key={key}
                                                onClick={() => setSelected(date)}
                                                className={[
                                                    'h-20 rounded-lg border p-1 flex flex-col items-start transition-colors cursor-pointer text-left overflow-hidden',
                                                    isSelected
                                                        ? 'border-emerald-500 bg-emerald-50'
                                                        : 'border-gray-100 bg-white hover:bg-gray-50',
                                                ].join(' ')}
                                            >
                                                <span className={[
                                                    'text-xs font-medium leading-none px-0.5',
                                                    isToday ? 'text-emerald-600' : 'text-gray-900',
                                                ].join(' ')}>
                                                    {date.getDate()}
                                                </span>

                                                <div className="mt-auto w-full space-y-px">
                                                    {/* Own bookings — full detail. */}
                                                    {mine.slice(0, 2).map((b) => (
                                                        <div
                                                            key={b.uuid}
                                                            className="w-full px-1 rounded bg-emerald-100 text-emerald-800 text-[10px] leading-4 font-medium truncate"
                                                            title={b.vehicleDisplayName || b.vehicleRego}
                                                        >
                                                            {b.vehicleRego || 'Booked'}
                                                        </div>
                                                    ))}
                                                    {mine.length > 2 && (
                                                        <div className="text-[10px] leading-4 text-emerald-700 font-medium px-1">
                                                            +{mine.length - 2}
                                                        </div>
                                                    )}

                                                    {/*
                                                      Other customers' bookings — anonymous.
                                                      The backend sends ONLY date/time/duration for
                                                      these, so there is genuinely nothing here to
                                                      reveal — just how busy the workshop is. This is
                                                      not a CSS blur over real data; the data was
                                                      never sent.
                                                    */}
                                                    {occupied > 0 && (
                                                        <div
                                                            className="w-full px-1 rounded bg-gray-100 text-gray-400 text-[10px] leading-4 truncate select-none"
                                                            title={`${occupied} other ${occupied === 1 ? 'booking' : 'bookings'} — workshop is busy`}
                                                        >
                                                            ▪▪▪ {occupied}
                                                        </div>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Legend */}
                                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100 px-1">
                                    <div className="flex items-center gap-1.5">
                                        <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200" />
                                        <span className="text-xs text-gray-500">Your bookings</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="w-3 h-3 rounded bg-gray-100 border border-gray-200" />
                                        <span className="text-xs text-gray-500">Workshop busy</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Day detail */}
                    <div>
                        <DayPanel
                            date={selected}
                            bookings={selectedDay?.mine ?? []}
                            occupiedCount={selectedDay?.occupied ?? 0}
                            onBook={() => { dispatch(clearFleetBookingErrors()); setDialogOpen(true); }}
                            onCancel={handleCancel}
                            cancellingUuid={cancellingUuid}
                            cancelError={cancelError}
                        />
                    </div>
                </div>
            </div>

            <NewBookingDialog
                open={dialogOpen}
                date={selected || today}
                vehicles={vehicles}
                serviceCategories={serviceCategories}
                serviceProfiles={serviceProfiles}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleCreate}
                submitting={creating}
                error={createError}
            />
        </Layout>
    );
};

export default FleetBookingsPage;